import React, { useEffect, useState } from "react";
import { User, Phone, Mail, Send, MessageSquare } from "lucide-react";

export const BathroomLanding = () => {
  const [aboutData, setAboutData] = useState([]);
  const [extraDetails, setExtraDetails] = useState({});

  // ================= FETCH DATA =================
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          "https://webadmin.rigve.com/api/get_about",
        );
        const result = await response.json();

        if (result.status === 1) {
          setAboutData(result.data);
          setExtraDetails(result.extra_details);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  // ================= STATS =================
  const stats = [
    {
      number: extraDetails?.total_awwards || "15",
      label: "Awards",
    },
    {
      number: extraDetails?.total_projects || "1024",
      label: "Projects",
    },
    {
      number: extraDetails?.total_clients || "2024",
      label: "Happy Clients",
    },
  ];

  // ================= FORM STATE =================
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    consent: false,
  });

  // ================= ERROR STATE =================
  const [errors, setErrors] = useState({});

  // ================= HANDLE CHANGE =================
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "phone" && value.length > 10) return;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error while typing
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  // ================= HANDLE SUBMIT =================
  const handleSubmit = async (e) => {
    e.preventDefault();

    let newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = "Enter valid 10 digit phone number";
    }

    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Enter valid email address";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }

    if (!formData.consent) {
      newErrors.consent = "Please accept this field";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const response = await fetch(
        `https://webadmin.rigve.com/api/save_contact?name=${encodeURIComponent(
          formData.name,
        )}&phone=${encodeURIComponent(
          formData.phone,
        )}&email=${encodeURIComponent(
          formData.email,
        )}&msg=${encodeURIComponent(formData.message)}`,
      );

      const result = await response.json();

      if (result.status === 1) {
        alert("Submitted Successfully");
        setFormData({
          name: "",
          phone: "",
          email: "",
          message: "",
          consent: false,
        });
        setErrors({});
      }
    } catch (error) {
      console.error(error);
    }
  };

  const data = aboutData?.[1] || {};
  const desc = data?.description?.split("\r\n\r\n") || [];

  return (
    <main>
      {/* ================= HERO SECTION ================= */}
      <section className="relative bg-gradient-to-b from-white to-orange-50 py-16 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12 md:mb-16">
            <p className="text-orange-600 font-semibold tracking-widest uppercase text-sm">
              {data?.sub_title}
            </p>

            <h6 className="mt-3 mb-6 fs-21 font-bold text-gray-900 leading-tight">
              {data?.title}
            </h6>

            <div className="w-24 h-1 bg-orange-500 mx-auto rounded-full"></div>
          </div>

          {/* Main Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="bg-white shadow-xl rounded-2xl p-6 md:p-10 space-y-6 border border-gray-100">
              <div className="space-y-4 text-gray-700">
                {desc[0] && (
                  <p className="text-md leading-relaxed">{desc[0]}</p>
                )}

                {data?.image_title && (
                  <h6 className="text-lg font-bold text-gray-900 border-l-4 border-orange-500 pl-3">
                    {data.image_title}
                  </h6>
                )}

                {desc[1] && (
                  <p className="text-md leading-relaxed">{desc[1]}</p>
                )}
              </div>

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <a
                  href="Products/5/Interior%20Wall%20Textures"
                  className="flex-1 text-center px-3 py-3 rounded-full bg-orange-500 text-white font-semibold shadow-md hover:bg-orange-600 hover:scale-105 transition"
                >
                  Explore Premium Designs
                </a>

                <a
                  href="/contact"
                  className="flex-1 text-center px-3 py-3 rounded-full border border-gray-300 text-gray-700 font-semibold hover:bg-gray-100 transition"
                >
                  Get Consultation
                </a>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 pt-6 border-t border-gray-200">
                {stats.map((stat, i) => (
                  <div key={i} className="text-center">
                    <div className="text-xl font-bold text-gray-900">
                      {stat.number}
                    </div>

                    <div className="text-md text-gray-500 mt-1">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Image */}
            <div className="relative group">
              <div className="rounded-2xl overflow-hidden shadow-2xl transform group-hover:scale-[1.02] transition duration-500">
                {data?.image && (
                  <img
                    src={data.image}
                    alt={data?.title || "Luxury bathroom interior"}
                    className="w-full h-[300px] sm:h-[420px] md:h-[520px] lg:h-[600px] object-cover"
                  />
                )}
              </div>

              <div className="absolute -bottom-6 -left-6 w-28 h-28 bg-orange-500 opacity-20 rounded-full blur-2xl"></div>
              <div className="absolute -top-6 -right-6 w-20 h-20 bg-gray-900 opacity-10 rounded-full blur-xl"></div>
              <div className="absolute top-6 left-6 bg-white px-4 py-2 rounded-full shadow-md text-sm font-semibold text-gray-700">
                ✨ Premium Quality Interiors
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= CONTACT FORM SECTION ================= */}
      <section className="w-full bg-[#f8fafc] py-16 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Heading */}
          <div className="text-center mb-14">
            <h2 className="fs-21 font-bold text-gray-900 leading-tight">
              Get In Touch For
              <span className="text-orange-600"> Wall Texture Solutions</span>
            </h2>

            <p className="max-w-2xl mx-auto mt-3 text-gray-600 text-md leading-8">
              Fill out the form below and our team will connect with you for
              creative wall texture designs and premium interior solutions.
            </p>
          </div>

          {/* Form Wrapper */}
          <div className="grid grid-cols-1 lg:grid-cols-2 overflow-hidden rounded-[32px] shadow-2xl bg-white">
            {/* Left Side */}
            <div className="relative bg-gradient-to-br from-orange-500 to-yellow-600 p-10 text-white overflow-hidden">
              <div className="relative z-10">
                <h3 className="fs-21 font-bold mb-6 leading-tight">
                  Let’s Build Creative Interiors Together
                </h3>

                <p className="text-white/90 leading-8 text-md mb-10">
                  We specialize in modern decorative wall textures and luxury
                  finishes for homes and commercial spaces.
                </p>

                <div className="space-y-5">
                  {[
                    "Premium Decorative Wall Finishes",
                    "Modern Luxury Texture Designs",
                    "Residential & Commercial Solutions",
                    "Free Expert Consultation",
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-4 bg-white/10 rounded-2xl px-5 py-4 backdrop-blur-md"
                    >
                      <div className="w-3 h-3 flex-shrink-0 rounded-full bg-white"></div>
                      <p className="text-white">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ================= FORM ================= */}
            <div className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name
                  </label>

                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      className={`w-full h-12 text-sm pl-12 pr-4 rounded-2xl border outline-none transition-all duration-300 ${
                        errors.name
                          ? "border-red-500 focus:ring-red-100"
                          : "border-gray-200 focus:border-orange-500 focus:ring-orange-100"
                      } focus:ring-4`}
                    />
                  </div>

                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address
                  </label>

                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter your email"
                      className={`w-full text-sm h-12 pl-12 pr-4 rounded-2xl border outline-none transition-all duration-300 ${
                        errors.email
                          ? "border-red-500 focus:ring-red-100"
                          : "border-gray-200 focus:border-orange-500 focus:ring-orange-100"
                      } focus:ring-4`}
                    />
                  </div>

                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number
                  </label>

                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Enter your phone number"
                      className={`w-full h-12 text-sm pl-12 pr-4 rounded-2xl border outline-none transition-all duration-300 ${
                        errors.phone
                          ? "border-red-500 focus:ring-red-100"
                          : "border-gray-200 focus:border-orange-500 focus:ring-orange-100"
                      } focus:ring-4`}
                    />
                  </div>

                  {errors.phone && (
                    <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                  )}
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Message
                  </label>

                  <div className="relative">
                    <MessageSquare className="absolute left-4 top-5 w-5 h-5 text-gray-400" />
                    <textarea
                      rows="5"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Write your requirement..."
                      className={`w-full text-sm pl-12 pr-4 py-4 rounded-2xl border outline-none transition-all duration-300 resize-none ${
                        errors.message
                          ? "border-red-500 focus:ring-red-100"
                          : "border-gray-200 focus:border-orange-500 focus:ring-orange-100"
                      } focus:ring-4`}
                    ></textarea>
                  </div>

                  {errors.message && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.message}
                    </p>
                  )}
                </div>

                {/* Consent Checkbox */}
                <div>
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="consent"
                      name="consent"
                      checked={formData.consent}
                      onChange={handleChange}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                    />
                    <label htmlFor="consent" className="text-sm text-gray-600">
                      I agree to allow this website to store and process my
                      submitted info.
                    </label>
                  </div>
                  {errors.consent && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.consent}
                    </p>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="w-full h-14 rounded-2xl bg-orange-500 hover:bg-orange-600 text-white font-semibold text-md flex items-center justify-center gap-3 transition-all duration-300 shadow-lg"
                >
                  Send Inquiry
                  <Send className="w-5 h-5" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};
